import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSollicituds } from '@/api/planificacio';
import {
  elementActiu,
  pestanyaActiva,
  pestanyesSeccio,
  type SeccioNav,
} from '@/navigation/navigation';
import { useMe } from '@/permisos/MeContext';

/** Comptadors de pestanya: sol·licituds de canvi de torn pendents a Guàrdies. */
function useComptadorsPestanya(seccio: SeccioNav): Partial<Record<string, number>> {
  const { pot } = useMe();
  const sollicituds = useSollicituds(
    'PENDENT',
    seccio === 'guardies' && pot('cai.sollicituds', 'CONSULTAR'),
  );
  const pendents = sollicituds.data?.length ?? 0;
  return { sollicituds: pendents > 0 ? pendents : undefined };
}

/**
 * Barra de pestanyes d'una secció (sota el títol). Són enllaços de navegació entre rutes, per
 * això es marquen amb `aria-current="page"` dins d'un `<nav>` (no és un `tablist` de panells).
 * Només es mostra si l'usuari té accés a dues pestanyes o més; les pestanyes que agrupen
 * diverses pantalles (Registre: Auditoria + Traçabilitat) hi afegeixen sub-pestanyes.
 */
export function PestanyesSeccio({ seccio }: { seccio: SeccioNav }) {
  const { t } = useTranslation('comu');
  const { me } = useMe();
  const { pathname } = useLocation();
  const comptadors = useComptadorsPestanya(seccio);
  const pestanyes = pestanyesSeccio(me, seccio);
  const activa = pestanyaActiva(pathname, seccio);
  const subs = pestanyes.find((p) => p.clau === activa)?.elements ?? [];
  const sub = elementActiu(pathname, subs);
  if (pestanyes.length < 2 && subs.length < 2) return null;
  return (
    <>
      {pestanyes.length >= 2 ? (
        <nav className="pestanyes" aria-label={t(`nav.seccions.${seccio}`)} data-seccio={seccio}>
          {pestanyes.map((p) => {
            const n = comptadors[p.clau];
            return (
              <Link
                key={p.clau}
                to={p.ruta}
                className="pestanyes__item"
                aria-current={p.clau === activa ? 'page' : undefined}
                data-pestanya={p.clau}
              >
                {t(`nav.pestanyes.${p.clau}`)}
                {n ? <span className="pestanyes__compte">· {n}</span> : null}
              </Link>
            );
          })}
        </nav>
      ) : null}
      {subs.length >= 2 ? (
        <nav className="segmented subpestanyes" aria-label={t(`nav.pestanyes.${activa ?? ''}`)}>
          {subs.map((e) => (
            <Link key={e.clau} to={e.ruta} aria-current={sub?.clau === e.clau ? 'page' : undefined}>
              {t(`nav.${e.clau}`, { ns: e.ns })}
            </Link>
          ))}
        </nav>
      ) : null}
    </>
  );
}
